/**
 * Signup Modal — Accessible multi-step Klaviyo signup popup.
 *
 * Ported from the GOAT production theme into Horizon. Behaviour is preserved
 * verbatim; only the bootstrap is adapted to Horizon's module-script loading
 * (auto-init + theme-editor re-init) instead of prod's bundled entry point.
 *
 * Uses the native <dialog> element for built-in accessibility:
 *   - focus trapping
 *   - Escape key to close
 *   - inert background
 *   - ::backdrop overlay
 *
 * ADA features:
 *   - Proper <label> elements for all inputs
 *   - aria-describedby for error messages
 *   - aria-live regions for dynamic content & step announcements
 *   - Visible focus indicators on all interactive elements
 *   - Keyboard-navigable skip/close controls
 *   - Focus restored to page on close (not browser chrome)
 *   - Reduced motion support via prefers-reduced-motion
 */

const COOKIE_PREFIX = 'goat_signup_modal';

const SignupModal = {
  dialog: null,
  currentStep: 1,
  totalSteps: 2,
  email: '',
  hasSubmitted: false,
  dynamicCode: null,
  previouslyFocused: null,

  init() {
    const dialog = document.querySelector('.js-signup-modal');
    if (!dialog) return;
    // Guard against double-binding: the module can run again on
    // shopify:section:load in the theme editor, but each reload hands us a
    // fresh dialog node without this flag.
    if (dialog.dataset.signupBound === 'true') return;
    dialog.dataset.signupBound = 'true';

    this.dialog = dialog;
    // Reset per-instance state (matters when the editor re-renders the section)
    this.currentStep = 1;
    this.hasSubmitted = false;
    this.dynamicCode = null;
    this.previouslyFocused = null;
    this._emailSubmitting = false;
    this._phoneSubmitting = false;

    const d = this.dialog.dataset;
    this.companyId = d.klaviyoCompanyId || '';
    this.listId = d.klaviyoListId || '';
    this.smsListId = d.klaviyoSmsListId || '';
    this.dismissDays = parseInt(d.dismissDays, 10) || 15;
    this.submitDays = parseInt(d.submitDays, 10) || 90;
    this.repeatBehavior = d.repeatBehavior || 'days';
    this.formVersionId = d.klaviyoFormVersionId || '';
    this.fallbackCode = this.decodeFallback(
      window.__goatSignupModalData && window.__goatSignupModalData.fallback,
    );

    this.teaser = document.querySelector('.js-signup-modal-teaser');

    const smsStep = this.dialog.querySelector(
      '[data-step="2"][aria-label*="phone"]',
    );
    this.totalSteps = smsStep ? 3 : 2;

    this.bindEvents();
    this.scheduleTrigger();
  },

  closeReason() {
    return this.hasSubmitted ? 'submit' : 'dismiss';
  },

  isSuppressed() {
    return (
      this.getStorage(`${COOKIE_PREFIX}_dismiss`) ||
      this.getStorage(`${COOKIE_PREFIX}_submit`)
    );
  },

  bindEvents() {
    // Close button
    const closeBtn = this.dialog.querySelector('.js-signup-modal-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close(this.closeReason()));
    }

    // Backdrop click — detect clicks outside the dialog's visible area
    this.dialog.addEventListener('click', (e) => {
      const rect = this.dialog.getBoundingClientRect();
      const outside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;
      if (outside) this.close(this.closeReason());
    });

    // Escape key
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close(this.closeReason());
    });

    // Step 1: Email
    const emailSubmit = this.dialog.querySelector(
      '.js-signup-modal-email-submit',
    );
    const emailInput = this.dialog.querySelector('.js-signup-modal-email');
    if (emailSubmit) {
      emailSubmit.addEventListener('click', () => this.handleEmailSubmit());
    }
    if (emailInput) {
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleEmailSubmit();
        }
      });
      emailInput.addEventListener('input', () => this.clearError('email'));
    }

    // Step 2: Phone
    const phoneSubmit = this.dialog.querySelector(
      '.js-signup-modal-phone-submit',
    );
    const phoneInput = this.dialog.querySelector('.js-signup-modal-phone');
    if (phoneSubmit) {
      phoneSubmit.addEventListener('click', () => this.handlePhoneSubmit());
    }
    if (phoneInput) {
      phoneInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handlePhoneSubmit();
        }
      });
      phoneInput.addEventListener('input', () => {
        this.clearError('phone');
        this.formatPhoneInput(phoneInput);
      });
    }

    // Step 2: Skip SMS — dismiss modal, show teaser
    const skipSms = this.dialog.querySelector('.js-signup-modal-skip-sms');
    if (skipSms) {
      skipSms.addEventListener('click', () => this.close(this.closeReason()));
    }

    // Step 3: Copy code
    const copyBtn = this.dialog.querySelector('.js-signup-modal-copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => this.copyDiscountCode(copyBtn));
    }

    // Step 2: Country select
    const countrySelect = this.dialog.querySelector('.js-signup-modal-country');
    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        const opt = countrySelect.options[countrySelect.selectedIndex];
        const flagEl = this.dialog.querySelector('.js-signup-modal-flag');
        const codeEl = this.dialog.querySelector(
          '.js-signup-modal-country-code',
        );
        if (flagEl) {
          // Flag is decorative (alt="" aria-hidden): the <select> already
          // exposes the country, so don't add a competing accessible name.
          flagEl.src = `https://flagcdn.com/w40/${opt.dataset.flag}.png`;
        }
        if (codeEl) codeEl.textContent = opt.value;
      });
    }

    // Teaser
    if (this.teaser) {
      const teaserOpen = this.teaser.querySelector(
        '.js-signup-modal-teaser-open',
      );
      const teaserClose = this.teaser.querySelector(
        '.js-signup-modal-teaser-close',
      );
      if (teaserOpen) {
        teaserOpen.addEventListener('click', () => {
          this.hideTeaser();
          this.open(true);
        });
      }
      if (teaserClose) {
        teaserClose.addEventListener('click', () => {
          this.hideTeaser();
          this.setStorage(
            `${COOKIE_PREFIX}_teaser_dismiss`,
            '1',
            this.dismissDays,
          );
        });
      }
    }
  },

  // Explicit opt-in: merchant ticked "Test mode" to force the modal open so
  // they can style it. This is the ONLY thing that should auto-open it in the
  // editor.
  isPreviewMode() {
    return this.dialog.dataset.testMode === 'true';
  },

  // Any theme-editor context (the storefront preview iframe inside the admin).
  isDesignMode() {
    return !!window.Shopify?.designMode;
  },

  scheduleTrigger() {
    // Preview toggle on — force it open for styling.
    if (this.isPreviewMode()) {
      this.open();
      return;
    }

    // In the editor without the preview toggle, never auto-open: it would pop
    // up on every reload while the merchant is editing. They can still open it
    // via the teaser if they need to inspect it, or flip "Test mode" on.
    if (this.isDesignMode()) {
      return;
    }

    if (this.isSuppressed()) {
      if (
        this.getStorage(`${COOKIE_PREFIX}_dismiss`) &&
        !this.getStorage(`${COOKIE_PREFIX}_teaser_dismiss`)
      ) {
        this.showTeaser();
      }
      return;
    }

    const delay = parseInt(this.dialog.dataset.triggerDelay, 10) || 5;
    this.delayTimer = setTimeout(() => this.open(), delay * 1000);

    // Exit intent (desktop only)
    if (
      this.dialog.dataset.exitIntent === 'true' &&
      window.matchMedia('(pointer: fine)').matches
    ) {
      this.exitIntentHandler = (e) => {
        if (e.clientY <= 5 && !this.dialog.open) {
          this.open();
          document.removeEventListener('mouseout', this.exitIntentHandler);
        }
      };
      document.addEventListener('mouseout', this.exitIntentHandler);
    }
  },

  open(fromTeaser) {
    if (this.dialog.open) return;
    if (!fromTeaser && !this.isPreviewMode() && this.isSuppressed()) return;

    if (this.delayTimer) clearTimeout(this.delayTimer);

    this.previouslyFocused = document.activeElement;
    this.dialog.showModal();

    this.focusFirstInteractive(
      this.dialog.querySelector('.signup-modal__step.is-active'),
    );
    this.pushDataLayer('signup_modal_open', { step: this.currentStep });
  },

  close(reason) {
    if (!this.dialog.open) return;
    this.dialog.close();

    // Restore focus for assistive tech (WCAG 2.4.3). Priority order:
    //   1. Cookie consent banner
    //   2. "Enable accessibility" widget button
    //   3. The element focused before the modal opened
    //   4. First focusable element on the page
    // The consent banner and a11y widget are injected by Shopify / 3rd-party
    // apps (they're absent from theme source), so we probe known selectors and
    // only accept a currently-visible match before falling back.
    const fallbackTarget =
      this.previouslyFocused && this.previouslyFocused !== document.body
        ? this.previouslyFocused
        : document.querySelector('a[href], button, input, [tabindex="0"]');
    if (!this.focusElement(this.getPostCloseFocusTarget())) {
      this.focusElement(fallbackTarget);
    }
    this.previouslyFocused = null;

    // Don't persist dismiss/submit cookies from inside the editor — neither an
    // explicit preview nor incidental browsing should suppress the real popup.
    if (!this.isPreviewMode() && !this.isDesignMode()) {
      if (reason === 'dismiss') {
        this.setStorage(`${COOKIE_PREFIX}_dismiss`, '1', this.dismissDays);
        this.pushDataLayer('signup_modal_dismiss', { step: this.currentStep });
      } else if (reason === 'submit') {
        this.setStorage(`${COOKIE_PREFIX}_submit`, '1', this.submitDays);
        this.pushDataLayer('signup_modal_complete', {});
      }
    }

    if (this.exitIntentHandler) {
      document.removeEventListener('mouseout', this.exitIntentHandler);
    }

    // Show teaser unless user is on the last step (they're done) or teaser was closed
    const onLastStep = this.currentStep === this.totalSteps;
    if (!onLastStep && !this.getStorage(`${COOKIE_PREFIX}_teaser_dismiss`)) {
      this.showTeaser();
    }
  },

  /**
   * Resolve the highest-priority post-close focus target: a visible cookie
   * consent banner, else a visible "Enable accessibility" widget button. These
   * live outside theme source (Shopify / app injected), so we probe a list of
   * known selectors and only accept a currently-rendered element. Returns null
   * when none are present, so close() can fall back to the prior focus.
   */
  getPostCloseFocusTarget() {
    const consentSelectors = [
      '#shopify-pc__banner',
      '#onetrust-banner-sdk',
      '#truste-consent-track',
      '[id*="cookie-banner" i]',
      '[id*="cookie-consent" i]',
      '[class*="cookie-banner" i]',
      '[class*="cookie-consent" i]',
      '[aria-label*="cookie" i]',
    ];
    const a11ySelectors = [
      '#userwayAccessibilityIcon',
      '.acsb-trigger',
      '#acsbBtn',
      '#ae-launcher',
      'button[aria-label*="accessibility" i]',
      'button[title*="accessibility" i]',
    ];

    const isVisible = (el) => {
      if (!el) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.hasAttribute('inert') || el.closest('[inert]')) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const findFirst = (selectors) => {
      for (const selector of selectors) {
        let el = null;
        try {
          el = document.querySelector(selector);
        } catch {
          el = null;
        }
        if (isVisible(el)) return el;
      }
      return null;
    };

    return findFirst(consentSelectors) || findFirst(a11ySelectors);
  },

  /**
   * Move focus to an element, making non-focusable containers (e.g. a consent
   * banner div) programmatically focusable first. Guarded so it never throws.
   * Returns true only when focus actually landed on the element.
   */
  focusElement(el) {
    if (!el) return false;
    const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
    const isNativelyFocusable =
      focusableTags.includes(el.tagName) ||
      el.hasAttribute('tabindex') ||
      el.isContentEditable;
    if (!isNativelyFocusable) el.setAttribute('tabindex', '-1');
    try {
      el.focus();
    } catch {
      return false;
    }
    return document.activeElement === el;
  },

  showTeaser() {
    if (this.teaser) this.teaser.style.display = 'block';
  },

  hideTeaser() {
    if (this.teaser) this.teaser.style.display = 'none';
  },

  goToStep(stepNumber) {
    const steps = this.dialog.querySelectorAll('.signup-modal__step');
    steps.forEach((step) => {
      step.classList.remove('is-active');
      step.style.display = 'none';
    });

    const targetStep = this.dialog.querySelector(`[data-step="${stepNumber}"]`);
    if (!targetStep) return;

    targetStep.classList.add('is-active');
    targetStep.style.display = 'flex';
    this.currentStep = stepNumber;

    // Inject coupon code on success step — dynamic code from Klaviyo, or
    // fallback if the API call failed. Code is never rendered server-side
    // to keep it out of crawler-visible page source.
    if (stepNumber === this.totalSteps) {
      const code = this.dynamicCode || this.fallbackCode;
      if (code) this.updateDiscountCodeDisplay(code);
    }

    // Announce step change to screen readers
    const announce = this.dialog.querySelector('.js-signup-modal-announce');
    if (announce) {
      announce.textContent =
        targetStep.getAttribute('aria-label') || `Step ${stepNumber}`;
    }

    this.focusStepTop(targetStep);
    this.pushDataLayer('signup_modal_step', { step: stepNumber });
  },

  /** Focus the first interactive element inside a container */
  focusFirstInteractive(container) {
    if (!container) return;
    requestAnimationFrame(() => {
      const el = container.querySelector('input, button, a, [tabindex="0"]');
      if (el) el.focus();
    });
  },

  /** Move focus to the heading at the top of a step (keeps focus order logical after a step change) */
  focusStepTop(container) {
    if (!container) return;
    requestAnimationFrame(() => {
      const heading =
        container.querySelector('[data-step-heading]') ||
        container.querySelector('h2');
      (heading || container).focus();
    });
  },

  async handleEmailSubmit() {
    // Prevent double-submit
    if (this._emailSubmitting) return;

    const input = this.dialog.querySelector('.js-signup-modal-email');
    const email = input?.value?.trim();

    if (!email || !this.isValidEmail(email)) {
      this.showError('email', 'Please enter a valid email address.');
      input?.focus();
      return;
    }

    this.email = email;
    this._emailSubmitting = true;
    const submitBtn = this.dialog.querySelector(
      '.js-signup-modal-email-submit',
    );

    try {
      this.setLoading(submitBtn, true);

      const [, couponCode] = await Promise.all([
        this.subscribeToKlaviyo({ email }).catch(() => {
          // subscribe failed — coupon still proceeds
        }),
        this.fetchOnsiteCoupon(email).catch(() => null),
      ]);

      if (couponCode) {
        this.dynamicCode = couponCode;
      }
    } catch {
      // both failed — proceed to next step regardless
    } finally {
      this.setLoading(submitBtn, false);
      this._emailSubmitting = false;
    }

    this.hasSubmitted = true;

    this.pushDataLayer('newsletter_signup', {
      method: 'popup',
      signup_location: 'Signup Modal / Step 1',
    });

    this.goToStep(this.totalSteps > 2 ? 2 : this.totalSteps);
  },

  async handlePhoneSubmit() {
    if (this._phoneSubmitting) return;

    const input = this.dialog.querySelector('.js-signup-modal-phone');
    const countrySelect = this.dialog.querySelector('.js-signup-modal-country');
    const countryCode = countrySelect?.value || '+1';
    const digits = (input?.value?.trim() || '').replace(/\D/g, '');

    // If phone is empty, skip SMS and go to success step
    if (!digits) {
      this.goToStep(this.totalSteps);
      return;
    }

    // If phone is entered but too short, show error
    if (digits.length < 7) {
      this.showError('phone', 'Please enter a valid phone number.');
      input?.focus();
      return;
    }

    this._phoneSubmitting = true;
    const phone = `${countryCode}${digits}`;
    const submitBtn = this.dialog.querySelector(
      '.js-signup-modal-phone-submit',
    );

    try {
      this.setLoading(submitBtn, true);
      await this.subscribeToKlaviyo({ phone_number: phone, sms_consent: true });
    } catch {
      // SMS failed — proceed to success step regardless
    } finally {
      this.setLoading(submitBtn, false);
      this._phoneSubmitting = false;
    }

    this.pushDataLayer('sms_signup', {
      method: 'popup',
      signup_location: 'Signup Modal / Step 2',
    });

    this.goToStep(this.totalSteps);
  },

  // ─── Klaviyo API ────────────────────────────────────────────

  async subscribeToKlaviyo(data) {
    if (!this.companyId) {
      // companyId not configured — skip API call
      return;
    }

    const targetListId = data.sms_consent
      ? this.smsListId || this.listId
      : this.listId;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Signup Modal',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: this.email || undefined,
                phone_number: data.phone_number || undefined,
              },
            },
          },
        },
        relationships: {},
      },
    };

    if (targetListId) {
      payload.data.relationships.list = {
        data: { type: 'list', id: targetListId },
      };
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok && response.status !== 202) {
      let detail = '';
      try {
        detail = JSON.stringify(await response.json());
      } catch {
        // response body wasn't JSON — leave detail empty
      }
      throw new Error(`Klaviyo API error ${response.status}: ${detail}`);
    }
  },

  async fetchOnsiteCoupon(email) {
    if (!this.companyId || !this.formVersionId) return null;

    try {
      const response = await fetch(
        'https://a.klaviyo.com/api/onsite/coupon-code',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            form_version_id: parseInt(this.formVersionId, 10),
            company: this.companyId,
            $email: email,
          }),
        },
      );

      const body = await response.json();
      if (!response.ok) return null;
      return body?.coupon_code || body?.code || null;
    } catch {
      return null;
    }
  },

  // ─── UI Helpers ─────────────────────────────────────────────

  updateDiscountCodeDisplay(code) {
    const codeText = this.dialog.querySelector('.js-signup-modal-code-text');
    const copyBtn = this.dialog.querySelector('.js-signup-modal-copy');
    // Do NOT set aria-label on the role="status" container: an accessible name
    // on a live region replaces its content for screen readers, garbling the
    // code into one spoken word. Let the visible text be announced/read instead.
    if (codeText) codeText.textContent = code;
    if (copyBtn) copyBtn.dataset.code = code;
  },

  showError(field, message) {
    const errorEl = this.dialog.querySelector(
      `.js-signup-modal-${field}-error`,
    );
    const inputEl = this.dialog.querySelector(`.js-signup-modal-${field}`);
    if (errorEl) errorEl.textContent = message;
    if (inputEl) {
      inputEl.classList.add('has-error');
      inputEl.setAttribute('aria-invalid', 'true');
    }
    if (field === 'phone') {
      const wrapper = this.dialog.querySelector(
        '.js-signup-modal-phone-wrapper',
      );
      if (wrapper) wrapper.classList.add('has-error');
    }
  },

  clearError(field) {
    const errorEl = this.dialog.querySelector(
      `.js-signup-modal-${field}-error`,
    );
    const inputEl = this.dialog.querySelector(`.js-signup-modal-${field}`);
    if (errorEl) errorEl.textContent = '';
    if (inputEl) {
      inputEl.classList.remove('has-error');
      inputEl.removeAttribute('aria-invalid');
    }
    if (field === 'phone') {
      const wrapper = this.dialog.querySelector(
        '.js-signup-modal-phone-wrapper',
      );
      if (wrapper) wrapper.classList.remove('has-error');
    }
  },

  setLoading(btn, loading) {
    if (!btn) return;
    btn.disabled = loading;
    if (loading) {
      btn.dataset.originalText = btn.textContent;
      btn.textContent = '...';
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.textContent = btn.dataset.originalText || btn.textContent;
      btn.removeAttribute('aria-busy');
    }
  },

  async copyDiscountCode(btn) {
    const code = btn?.dataset.code;
    if (!code) return;

    const tooltip = this.dialog.querySelector('.js-signup-modal-copy-tooltip');
    const statusEl = this.dialog.querySelector('.js-signup-modal-copy-status');

    try {
      await navigator.clipboard.writeText(code);
      btn.classList.add('is-copied');
      if (tooltip) tooltip.classList.add('is-visible');
      if (statusEl) statusEl.textContent = 'Coupon applied to checkout.';
      setTimeout(() => {
        btn.classList.remove('is-copied');
        if (tooltip) tooltip.classList.remove('is-visible');
        if (statusEl) statusEl.textContent = '';
      }, 3000);
    } catch {
      const codeText = this.dialog.querySelector('.js-signup-modal-code-text');
      if (codeText) {
        const range = document.createRange();
        range.selectNodeContents(codeText);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // Clipboard API unavailable (insecure context / permission denied). The
      // code is now selected — tell screen-reader & keyboard users how to copy
      // it so the action isn't a silent no-op (WCAG 4.1.3 Status Messages).
      if (statusEl) {
        statusEl.textContent = 'Press Ctrl+C or Command+C to copy the code.';
        setTimeout(() => {
          statusEl.textContent = '';
        }, 5000);
      }
    }
  },

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  },

  decodeFallback(encoded) {
    if (!encoded) return '';
    try {
      return atob(encoded);
    } catch {
      return '';
    }
  },

  formatPhoneInput(input) {
    const digits = input.value.replace(/\D/g, '');
    if (digits.length <= 3) {
      input.value = digits;
    } else if (digits.length <= 6) {
      input.value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      input.value = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
        6,
        10,
      )}`;
    }
  },

  // ─── Storage ────────────────────────────────────────────────

  setStorage(name, value, days) {
    if (this.repeatBehavior === 'session') {
      sessionStorage.setItem(name, value);
    } else {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
    }
  },

  getStorage(name) {
    if (this.repeatBehavior === 'session') {
      return sessionStorage.getItem(name);
    }
    return document.cookie
      .split('; ')
      .find((r) => r.startsWith(`${name}=`))
      ?.split('=')[1];
  },

  pushDataLayer(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  },
};

function boot() {
  SignupModal.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Re-init when the section is (re)loaded in the Shopify theme editor. Each
// reload replaces the dialog DOM, so init() binds the fresh node (its
// `signupBound` guard prevents double-binding on the live storefront).
document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector?.('.js-signup-modal')) SignupModal.init();
});
